/**
 *  @class
 *  @function MegaMenuSidebar
 */
if (!customElements.get('mega-menu-sidebar')) {
  class MegaMenuSidebar extends HTMLElement {
    constructor() {
      super();
      /** @type {NodeListOf<HTMLAnchorElement> | null} */
      this.buttons = null;
      /** @type {NodeListOf<HTMLUListElement> | null} */
      this.contentItems = null;
      /** @type {NodeListOf<HTMLImageElement> | null} */
      this.images = null;
    }

    connectedCallback() {
      this.buttons = this.querySelectorAll('.mega-menu-sidebar--button');
      this.mainScenarios = this.querySelectorAll('.mega-menu-sidebar--main-scenario');
      this.contentItems = this.querySelectorAll('.mega-menu-sidebar--content-inner');
      this.categoryTags = this.querySelectorAll('.mega-menu-sidebar--category-tag');
      this.subProducts = this.querySelectorAll('.mega-menu-sidebar--sub-products');
      this.images = this.querySelectorAll('img[data-src]');
      this.productLinks = this.querySelectorAll('.mega-menu-sidebar--product-link, .mega-menu-sidebar--view-all-link');

      // 获取 mega-menu 容器
      this.megaMenuContainer = this.closest('.mega-menu-container');

      // 添加鼠标离开事件 - 移除焦点以关闭弹窗
      if (this.megaMenuContainer) {
        this.megaMenuContainer.addEventListener('mouseleave', () => {
          // 移除当前焦点元素
          if (document.activeElement && this.megaMenuContainer.contains(document.activeElement)) {
            document.activeElement.blur();
          }
        });
      }

      // 添加点击事件监听 - 旧版按钮（如果存在）
      if (this.buttons && this.buttons.length > 0) {
        this.buttons.forEach((button, index) => {
          button.addEventListener('click', (event) => {
            this.onButtonClick(event, button, index);
          });

          // 保持鼠标悬停功能的兼容性
          button.addEventListener('mouseover', (event) => {
            this.onButtonHover(event, button, index);
          });
        });
      }

      // 添加点击事件监听 - 主场景卡片
      if (this.mainScenarios && this.mainScenarios.length > 0) {
        this.mainScenarios.forEach((scenario, index) => {
          scenario.addEventListener('click', (event) => {
            event.preventDefault();
            this.onScenarioClick(event, scenario, index);
          });

          // 鼠标悬停切换
          scenario.addEventListener('mouseover', (event) => {
            this.onScenarioHover(event, scenario, index);
          });
        });
      }

      // 添加点击事件监听 - 二级分类标签
      if (this.categoryTags && this.categoryTags.length > 0) {
        this.categoryTags.forEach((tag, index) => {
          tag.addEventListener('click', (event) => {
            event.preventDefault();
            this.onCategoryTagClick(event, tag, index);
            // 点击标签后，移除焦点
            tag.blur();
          });
        });
      }

      // 添加点击事件监听 - 产品链接和查看全部链接
      if (this.productLinks && this.productLinks.length > 0) {
        this.productLinks.forEach((link) => {
          link.addEventListener('click', (event) => {
            // 点击链接后，立即移除焦点以关闭 mega-menu
            link.blur();
            // 同时移除父级菜单项的焦点状态
            const parentMenuItem = this.closest('.menu-item-has-children');
            if (parentMenuItem) {
              parentMenuItem.focus();
              parentMenuItem.blur();
            }
          });
        });
      }

      // 处理图片懒加载（仅当lazySizes可用时）
      if (this.images && this.images.length > 0) {
        // 检查lazySizes是否可用
        if ('lazySizes' in window) {
          window.addEventListener('load', () => {
            this.images.forEach((image) => {
              if (window.lazySizes && window.lazySizes.loader) {
                window.lazySizes.loader.unveil(image);
              }
            });
          });
        } else {
          // 如果lazySizes不可用，直接加载图片
          this.images.forEach((image) => {
            const dataSrc = image.getAttribute('data-src');
            if (dataSrc) {
              image.src = dataSrc;
              image.removeAttribute('data-src');
            }
          });
        }
      }

      // 初始化分割线高度
      this.updateDividerHeight();
      
      // 延迟调用，确保DOM完全加载后再计算
      setTimeout(() => {
        this.updateDividerHeight();
      }, 100);
      
      // 页面加载完成后再次更新
      window.addEventListener('load', () => {
        this.updateDividerHeight();
      });
      
      // 监听窗口大小变化
      window.addEventListener('resize', () => {
        this.updateDividerHeight();
      });
    }

    /**
     * 处理按钮点击事件
     * @param {MouseEvent} event - 点击事件对象
     * @param {HTMLAnchorElement} button - 被点击的按钮
     * @param {number} index - 按钮索引
     */
    onButtonClick(event, button, index) {
      this.updateActiveButton(button);
      this.updateActiveContentByTarget(button);
    }

    /**
     * 处理按钮鼠标悬停事件
     * @param {MouseEvent} event - 鼠标悬停事件对象
     * @param {HTMLAnchorElement} button - 悬停的按钮
     * @param {number} index - 按钮索引
     */
    onButtonHover(event, button, index) {
      this.updateActiveButton(button);
      this.updateActiveContentByTarget(button);
    }

    /**
     * 处理主场景卡片点击事件
     * @param {MouseEvent} event - 点击事件对象
     * @param {HTMLElement} scenario - 被点击的主场景卡片
     * @param {number} index - 卡片索引
     */
    onScenarioClick(event, scenario, index) {
      this.updateActiveScenario(scenario);
      this.updateActiveContentByTarget(scenario);
    }

    /**
     * 处理主场景卡片鼠标悬停事件
     * @param {MouseEvent} event - 鼠标悬停事件对象
     * @param {HTMLElement} scenario - 悬停的主场景卡片
     * @param {number} index - 卡片索引
     */
    onScenarioHover(event, scenario, index) {
      this.updateActiveScenario(scenario);
      this.updateActiveContentByTarget(scenario);
    }

    /**
     * 更新活动按钮
     * @param {HTMLAnchorElement} activeButton - 活动按钮
     */
    updateActiveButton(activeButton) {
      // 移除所有按钮的active类
      if (this.buttons && this.buttons.length > 0) {
        this.buttons.forEach((btn) => {
          btn.classList.remove('active');
        });
      }

      // 为当前点击的按钮添加active类
      if (activeButton) {
        activeButton.classList.add('active');
      }
    }

    /**
     * 更新活动主场景卡片
     * @param {HTMLElement} activeScenario - 活动主场景卡片
     */
    updateActiveScenario(activeScenario) {
      // 移除所有主场景卡片的active类
      if (this.mainScenarios && this.mainScenarios.length > 0) {
        this.mainScenarios.forEach((scenario) => {
          scenario.classList.remove('active');
        });
      }

      // 为当前点击的主场景卡片添加active类
      if (activeScenario) {
        activeScenario.classList.add('active');
      }
    }

    /**
     * 处理二级分类标签点击事件
     * @param {MouseEvent} event - 点击事件对象
     * @param {HTMLElement} tag - 被点击的标签
     * @param {number} index - 标签索引
     */
    onCategoryTagClick(event, tag, index) {
      this.updateActiveCategoryTag(tag);
      this.updateActiveSubProductsByTarget(tag);
    }

    /**
     * 更新活动二级分类标签
     * @param {HTMLElement} activeTag - 活动标签
     */
    updateActiveCategoryTag(activeTag) {
      // 获取当前活动的内容区域
      const activeContent = this.querySelector('.mega-menu-sidebar--content-inner.active');
      
      if (activeContent) {
        // 只在当前活动内容区域内移除标签的active类
        const tagsInContent = activeContent.querySelectorAll('.mega-menu-sidebar--category-tag');
        tagsInContent.forEach((tag) => {
          tag.classList.remove('active');
        });

        // 为当前点击的标签添加active类
        if (activeTag) {
          activeTag.classList.add('active');
        }
      }
    }

    /**
     * 根据目标ID更新活动子产品容器
     * @param {HTMLElement} activeTag - 活动标签
     */
    updateActiveSubProductsByTarget(activeTag) {
      // 获取标签的data-sub-target属性值
      const targetId = activeTag ? activeTag.getAttribute('data-sub-target') : null;

      // 获取当前活动的内容区域
      const activeContent = this.querySelector('.mega-menu-sidebar--content-inner.active');

      if (activeContent && targetId) {
        // 只在当前活动内容区域内移除子产品容器的active类
        const subProductsInContent = activeContent.querySelectorAll('.mega-menu-sidebar--sub-products');
        subProductsInContent.forEach((item) => {
          item.classList.remove('active');
        });

        // 根据targetId查找对应的子产品容器并添加active类
        const targetElement = activeContent.querySelector(`#${targetId}`);
        if (targetElement) {
          targetElement.classList.add('active');
        }
        
        // 更新分割线高度
        this.updateDividerHeight();
      }
    }

    /**
     * 根据目标ID更新活动内容
     * @param {HTMLAnchorElement} activeButton - 活动按钮
     */
    updateActiveContentByTarget(activeButton) {
      // 获取按钮的data-target属性值
      const targetId = activeButton ? activeButton.getAttribute('data-target') : null;

      // 移除所有内容的active类
      if (this.contentItems) {
        this.contentItems.forEach((item) => {
          item.classList.remove('active');
        });
      }

      // 根据targetId查找对应的内容区域并添加active类
      if (targetId && this.contentItems) {
        this.contentItems.forEach((item) => {
          if (item.id === targetId) {
            item.classList.add('active');
          }
        });
      }

      // 更新分割线高度
      this.updateDividerHeight();
    }

    /**
     * 更新分割线高度，使其与右侧产品网格底部对齐
     */
    updateDividerHeight() {
      const mainScenarios = this.querySelector('.mega-menu-sidebar--main-scenarios');
      const activeContent = this.querySelector('.mega-menu-sidebar--content-inner.active');
      
      if (mainScenarios && activeContent) {
        const productsGrid = activeContent.querySelector('.mega-menu-sidebar--products-grid');
        
        if (productsGrid) {
          const productsGridRect = productsGrid.getBoundingClientRect();
          const mainScenariosRect = mainScenarios.getBoundingClientRect();
          
          // 计算分割线高度 = 产品网格底部位置 - 主场景容器顶部位置
          const dividerHeight = productsGridRect.bottom - mainScenariosRect.top;
          
          // 调试信息
          console.log('分割线高度计算:', {
            '产品网格底部': productsGridRect.bottom,
            '左侧容器顶部': mainScenariosRect.top,
            '分割线高度': dividerHeight,
            '产品网格高度': productsGridRect.height,
            '左侧容器高度': mainScenariosRect.height
          });
          
          // 设置CSS变量来控制分割线高度
          mainScenarios.style.setProperty('--divider-height', `${dividerHeight}px`);
          
          // 同时设置左侧卡片容器的高度，使其与分割线高度一致
          mainScenarios.style.height = `${dividerHeight}px`;
        }
      }
    }
  }

  customElements.define('mega-menu-sidebar', MegaMenuSidebar);
}
